import { Column, Divider, Typography } from '@/components'
import { ThemeColor } from '@/enums'
import { paths } from '@/routes/paths'
import { theme } from '@/theme'
import { Insurance } from '@/types/insurance'
import { HomeRecommendation } from '@/types/location'
import { formatMoney, useGoToPath } from '@/utils'
import { ResponsivePie } from '@nivo/pie'
import { ChevronRight } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChartData,
  ChartDataOutput,
  getInternalChartText,
  prepareChartData,
} from './helpers'
import {
  CustomTooltipComponent,
  LegendTitle,
  MiddleTextWrapper,
  MissingCoverageChartContainer,
  MissingCoverageContainer,
  MissingCoverageLegendColor,
  MissingCoverageLegendContainer,
  MissingCoverageLegendItem,
  MissingCoverageLegendItemButton,
  MissingCoverageLegendItemTitleRow,
  PriceText,
} from './styles'

interface MissingCoverageChartWidgetProps {
  allInsurances: Insurance[]
  allRecommendations: HomeRecommendation[]
  essentialCategoryIds: string[]
  isLoading: boolean
}

interface CustomTooltipProps {
  datum: {
    color: string
    data: {
      name: string
    }
    value: number
  }
}

interface InternalChartTextProps {
  grossPrice?: number
}

interface ChartLegendProps {
  data: ChartDataOutput
  isLoading: boolean
}

interface MissingCoverageChartProps {
  data: ChartData[]
  grossPrice?: number
}

export const MissingCoverageChartWidget = ({
  allInsurances = [],
  allRecommendations = [],
  essentialCategoryIds = [],
  isLoading,
}: MissingCoverageChartWidgetProps) => {
  const { t } = useTranslation()
  const chartData = useMemo(() => {
    return prepareChartData(
      allRecommendations,
      allInsurances,
      essentialCategoryIds,
      t,
    )
  }, [allInsurances, allRecommendations, essentialCategoryIds, t])

  if (isLoading) return null

  return (
    <MissingCoverageContainer>
      <MissingCoverageChart
        data={chartData.chartData}
        grossPrice={chartData.grossPrice}
      />
      <ChartLegend data={chartData} isLoading={isLoading} />
    </MissingCoverageContainer>
  )
}

const CustomTooltip = ({ datum }: CustomTooltipProps) => {
  const datumName = [datum.data.name, ':'].join('')

  return (
    <CustomTooltipComponent>
      <MissingCoverageLegendColor color={datum.color} />
      <Typography bold variant="p2Body">
        {datumName}
      </Typography>
      <Typography variant="p2Body">{formatMoney(datum.value)}</Typography>
    </CustomTooltipComponent>
  )
}

const InternalChartText = ({ grossPrice }: InternalChartTextProps) => {
  const { t } = useTranslation()

  const { integer, decimal } = getInternalChartText(grossPrice || 0)
  const formattedDecimal = `,${decimal}€`

  return (
    <MiddleTextWrapper>
      <span>{t('kpiOverview.insuranceExpenses')}</span>
      <Typography bold variant="h4">
        {integer}
        <Typography bold variant="badgeText">
          {formattedDecimal}
        </Typography>
      </Typography>
      <span>{t('kpiOverview.perYear')}</span>
    </MiddleTextWrapper>
  )
}

const LegendItem = ({
  item,
  onClick,
}: {
  item: ChartData
  onClick?: () => void
}) => {
  const { t } = useTranslation()

  return (
    <MissingCoverageLegendItem key={item.id}>
      <MissingCoverageLegendItemTitleRow>
        <MissingCoverageLegendColor color={item.color} />
        <MissingCoverageLegendItemButton onClick={onClick}>
          <Typography ellipsis variant="badgeText">
            {item.name}
          </Typography>
          {item.recommendationId && (
            <ChevronRight size={16} color={theme.color.b40} />
          )}
        </MissingCoverageLegendItemButton>
      </MissingCoverageLegendItemTitleRow>
      <PriceText bold variant="badgeText">
        {item.recommendationId
          ? t('missingCoverageIndicator.fromPrice', {
              price: formatMoney(item.value),
            })
          : formatMoney(item.value)}
      </PriceText>
    </MissingCoverageLegendItem>
  )
}

const ChartLegend = ({ data, isLoading }: ChartLegendProps) => {
  const { t } = useTranslation()
  const goToRecommendation = useGoToPath(paths.viewRecommendationWithKey)

  if (isLoading) return null

  return (
    <MissingCoverageLegendContainer>
      {data.existingInsuranceData.length > 0 && (
        <Column gap="8px">
          <LegendTitle variant="footnote" color={ThemeColor.b50}>
            {t('missingCoverageIndicator.existingCoverage')}
          </LegendTitle>
          {data.existingInsuranceData.map((item) => (
            <LegendItem key={item.id} item={item} />
          ))}
        </Column>
      )}
      {data.existingInsuranceData.length > 0 &&
        data.latestEssentialRecommendationsData.length > 0 && (
          <Divider color={ThemeColor.b30} />
        )}
      {data.latestEssentialRecommendationsData.length > 0 && (
        <Column gap="8px">
          <LegendTitle variant="footnote" color={ThemeColor.b50}>
            {t('missingCoverageIndicator.missingCoverage')}
          </LegendTitle>
          {data.latestEssentialRecommendationsData.map((item) => (
            <LegendItem
              key={item.id}
              item={item}
              onClick={() => goToRecommendation(item.recommendationId)}
            />
          ))}
        </Column>
      )}
    </MissingCoverageLegendContainer>
  )
}

const MissingCoverageChart = ({
  data,
  grossPrice,
}: MissingCoverageChartProps) => (
  <MissingCoverageChartContainer>
    <ResponsivePie
      activeOuterRadiusOffset={3}
      borderColor={{
        from: 'color',
      }}
      borderWidth={1}
      colors={{ datum: 'data.color' }}
      cornerRadius={3}
      data={data}
      enableArcLabels={false}
      enableArcLinkLabels={false}
      innerRadius={0.9}
      margin={{ right: 4, left: 4, top: 4, bottom: 4 }}
      padAngle={3}
      tooltip={({ datum }) => <CustomTooltip datum={datum} />}
      valueFormat={(value) => formatMoney(value)}
    />
    <InternalChartText grossPrice={grossPrice || 0} />
  </MissingCoverageChartContainer>
)
